import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {BaseChartDirective} from 'ng2-charts';
import {Chart, ChartData, ChartOptions, ChartType, registerables} from 'chart.js';

import {ApiService} from '../services/api.service';
import {RoleService} from '../services/role.service';

Chart.register(...registerables);

@Component({
    selector: 'app-sales-manager-dashboard',
    imports: [CommonModule, BaseChartDirective],
    templateUrl: './sales-manager-dashboard.component.html',
    styleUrls: ['./sales-manager-dashboard.component.css']
})
export class SalesManagerDashboardComponent implements OnInit {
  loading = false;
  error = false;
  stats: any = {
    total_leads: 0,
    new_leads: 0,
    today_follow_ups: 0,
    demo_scheduled: 0,
    won_leads: 0,
    lost_leads: 0,
  };
  salesPipeline: any = {
    total_leads: 0,
    new_leads: 0,
    contacted: 0,
    demo_scheduled: 0,
    demo_completed: 0,
    negotiation: 0,
    won: 0,
    lost: 0,
  };
  attention: any = {
    followups_due_today: 0,
    overdue_followups: 0,
    no_followup_leads: 0,
    demos_today: 0,
  };
  operations: any = {
    total_cameras: 0,
    active_cameras: 0,
    vehicle_logs_today: 0,
    inactive_customers: 0,
  };
  leadSources: any[] = [];
  partnerMetrics: any = {
    total: 0,
    active: 0,
    leads: 0,
    organizations: 0,
    installations: 0,
    pending_follow_ups: 0,
    top_performing: [],
  };

  cards = [
    { key: 'total_leads', label: 'Total Leads', helper: 'All active ANPR enquiries', icon: 'bi-briefcase', clickable: true },
    { key: 'new_leads', label: 'New Leads', helper: 'Fresh leads to qualify', icon: 'bi-stars', clickable: true },
    { key: 'contacted', label: 'Contacted', helper: 'Initial outreach completed', icon: 'bi-telephone', clickable: false },
    { key: 'won', label: 'Won Leads', helper: 'Converted opportunities', icon: 'bi-trophy', clickable: true },
  ];
  attentionCards = [
    { key: 'followups_due_today', label: 'Follow-ups Due Today', helper: 'Requires action today', icon: 'bi-calendar-check', route: 'lead-management/follow-ups', queryParamsFactory: () => ({ date: this.formatDate(new Date()) }) },
    { key: 'overdue_followups', label: 'Overdue Follow-ups', helper: 'Past due follow-up actions', icon: 'bi-exclamation-circle', route: 'lead-management/leads', queryParamsFactory: () => ({ followup: 'missing' }) },
    { key: 'no_followup_leads', label: 'No Follow-up Leads', helper: 'Open leads without next step', icon: 'bi-hourglass-split', route: 'lead-management/leads', queryParamsFactory: () => ({ followup: 'missing' }) },
    { key: 'demos_today', label: 'Demos Today', helper: 'Scheduled for today', icon: 'bi-camera-video', route: 'lead-management/demo-schedule', queryParamsFactory: () => ({ status: 'SCHEDULED', date: this.formatDate(new Date()) }) },
  ];
  operationsCards = [
    { key: 'total_cameras', label: 'Total Cameras', helper: 'Connected to your org', icon: 'bi-camera-video-off' },
    { key: 'active_cameras', label: 'Active Cameras', helper: 'Currently active', icon: 'bi-camera-video' },
    { key: 'vehicle_logs_today', label: 'Vehicle Logs Today', helper: 'Operational activity today', icon: 'bi-list-check' },
    { key: 'inactive_customers', label: 'Inactive Customers', helper: 'No vehicle logs in 48 hours', icon: 'bi-building' },
  ];
  partnerCards = [
    { key: 'total', label: 'Total Partners', helper: 'All sales and installation partners', icon: 'bi-people', route: 'admin/sales-installation-partners' },
    { key: 'active', label: 'Active Partners', helper: 'Onboarded or active partners', icon: 'bi-person-check', route: 'admin/sales-installation-partners' },
    { key: 'leads', label: 'Leads via Partners', helper: 'Leads sourced through partners', icon: 'bi-diagram-3', route: 'lead-management/leads', queryParamsFactory: () => ({ source: 'SALES_INSTALLATION_PARTNER' }) },
      {
          key: 'installations',
          label: 'Installations Handled',
          helper: 'Organizations installed via partners',
          icon: 'bi-tools',
          route: 'organization',
          queryParamsFactory: () => this.roleService.organizationsListingQueryParams()
      },
  ];
  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      }
    }
  };
  horizontalBarType: ChartType = 'bar';
  barType: ChartType = 'bar';

    constructor(private apiService: ApiService, private router: Router, private roleService: RoleService) {
    }

  ngOnInit(): void {
    this.loadDashboard();
  }

  loadDashboard(): void {
    this.loading = true;
    this.error = false;
    this.apiService.getLeadDashboard().subscribe({
      next: (response: any) => {
        this.stats = response || this.stats;
        this.salesPipeline = response?.sales_pipeline || this.salesPipeline;
        this.attention = response?.attention || this.attention;
        this.operations = response?.operations || this.operations;
        this.leadSources = response?.lead_sources || [];
        this.partnerMetrics = response?.partner_metrics || this.partnerMetrics;
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  goTo(route: string): void {
    this.router.navigate([route]);
  }

  openCard(cardKey: string): void {
    const statusMap: Record<string, string | null> = {
      total_leads: null,
      new_leads: 'NEW',
      won: 'WON',
    };

    const status = statusMap[cardKey];
    this.router.navigate(['lead-management/leads'], {
      queryParams: status ? { status } : {},
    });
  }

  openAttention(card: any): void {
    if (!card.route) {
      return;
    }
    this.router.navigate([card.route], {
      queryParams: card.queryParamsFactory ? card.queryParamsFactory() : {},
    });
  }

  openPartnerCard(card: any): void {
    if (!card.route) {
      return;
    }
    this.router.navigate([card.route], {
      queryParams: card.queryParamsFactory ? card.queryParamsFactory() : {},
    });
  }

  isCardClickable(card: any): boolean {
    return !!card.clickable;
  }

  get summaryCardValueMap(): Record<string, number> {
    return {
      total_leads: this.salesPipeline.total_leads || this.stats.total_leads || 0,
      new_leads: this.salesPipeline.new_leads || this.stats.new_leads || 0,
      contacted: this.salesPipeline.contacted || 0,
      won: this.salesPipeline.won || this.stats.won_leads || 0,
    };
  }

  get funnelChartData(): ChartData<'bar'> {
    return {
      labels: ['New', 'Contacted', 'Demo Scheduled', 'Demo Completed', 'Negotiation', 'Won'],
      datasets: [
        {
          label: 'Lead Funnel',
          data: [
            this.salesPipeline.new_leads || 0,
            this.salesPipeline.contacted || 0,
            this.salesPipeline.demo_scheduled || 0,
            this.salesPipeline.demo_completed || 0,
            this.salesPipeline.negotiation || 0,
            this.salesPipeline.won || 0,
          ],
          backgroundColor: ['#2563eb', '#0ea5e9', '#7c3aed', '#8b5cf6', '#f59e0b', '#10b981'],
          borderRadius: 8,
        }
      ]
    };
  }

  get funnelChartOptions(): ChartOptions {
    return {
      ...this.chartOptions,
      indexAxis: 'y',
    };
  }

  get leadSourceChartData(): ChartData<'bar'> {
    return {
      labels: this.leadSources.map((item) => item.source),
      datasets: [
        {
          label: 'Total Leads',
          data: this.leadSources.map((item) => item.count || 0),
          backgroundColor: '#60a5fa',
          borderRadius: 8,
        },
        {
          label: 'Won Leads',
          data: this.leadSources.map((item) => item.won || 0),
          backgroundColor: '#34d399',
          borderRadius: 8,
        }
      ]
    };
  }

  get hasLeadSourceData(): boolean {
    return this.leadSources.length > 0;
  }

  private formatDate(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
